/**
 * Busca fotos de um lugar no Google Places por placeId.
 * Requer endpoint /api/admin/place-photos no backend.
 */
import { apiUrl } from './api';
import { adminApiHeaders } from './adminApiAuth';

export interface PlacePhotosResult {
  fotos: string[];
  nome?: string;
}

/**
 * Extrai placeId de URLs comuns do Google Maps.
 * Ex: .../place/Nome/@lat,lng,17z/data=!4m2!3m1!1s0x... ou ...?place_id=ChIJ...
 */
export function extractPlaceIdFromUrl(url: string): string | null {
  const s = (url ?? '').trim();
  if (!s) return null;

  // place_id=ChIJ... ou place_id/ChIJ... (query ou path)
  const placeIdMatch = s.match(/place_id[=/]([A-Za-z0-9_-]+)/i);
  if (placeIdMatch) return placeIdMatch[1];

  // 1s0x... (hex) ou ChIJ... em data=
  const dataMatch = s.match(/!1s(0x[a-fA-F0-9]+|ChIJ[A-Za-z0-9_-]+)/);
  if (dataMatch) return dataMatch[1];

  // places/ChIJ... no path
  const pathMatch = s.match(/\/places\/(ChIJ[A-Za-z0-9_-]+)/);
  if (pathMatch) return pathMatch[1];

  // ChIJ... solto
  const chijMatch = s.match(/ChIJ[A-Za-z0-9_-]{20,}/);
  if (chijMatch) return chijMatch[0];

  return null;
}

/**
 * Busca fotos do lugar via API.
 * @param placeId - ID do lugar (ChIJ...) ou extraído da URL
 * @param fallbackQuery - Nome da cidade/lugar para busca por texto quando place_id não retorna fotos
 */
export async function fetchPlacePhotos(placeId: string, fallbackQuery?: string): Promise<PlacePhotosResult> {
  const id = (placeId ?? '').trim();
  const query = (fallbackQuery ?? '').trim();
  if (!id && !query) return { fotos: [] };

  const params = new URLSearchParams();
  if (id) params.set('placeId', id);
  if (query) params.set('fallbackQuery', query);
  const res = await fetch(apiUrl(`/api/admin/place-photos?${params.toString()}`), {
    headers: adminApiHeaders(),
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `Erro ${res.status}`);
  }

  const data = (await res.json()) as { fotos?: string[]; nome?: string };
  return { fotos: data.fotos ?? [], nome: data.nome };
}
